import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeftIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/base-components';
import { setFormPageToShow } from '@rsos/reduxDataStore';
import { setSelectedTool } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { trackCloseAirAmbulanceRequestForm } from '../../../gainsightTrackings/airAmbulanceTrackings';
import { FORM_PAGES } from '../AirAmbulanceForm/constants';
import AirAmbulanceSourceInfo from './AirAmbulanceSourceInfo';

interface AirAmbulanceState {
  airAmbulance: {
    formPageToShow: string | null;
    requests: Record<string, unknown>;
  };
}

interface EmergencyCallsState {
  emergencyCalls: {
    selectedCallerID: string | null;
  };
}

interface BackButtonProps {
  onClick: () => void;
}

const BackButton = ({ onClick }: BackButtonProps): JSX.Element => (
  <Box
    borderWidth="1px"
    borderStyle="solid"
    borderColor="heavyLineOutline"
    borderRadius="5px"
    data-name="tools-air-ambulance-form-exit-button"
    cursor="pointer"
    p={1}
    mr={3}
    mb={3}
    onClick={onClick}
  >
    <ArrowLeftIcon color="secondaryText" height={12} width={12} />
  </Box>
);

interface AirAmbulancePageHeaderProps {
  text?: string | null;
  subtext?: string | null;
  hideBackButton?: boolean;
  sourceLabel?: string | null;
  requestedAt?: string | null;
}

const AirAmbulancePageHeader = ({
  text = null,
  subtext = null,
  hideBackButton = false,
  sourceLabel = null,
  requestedAt = null,
}: AirAmbulancePageHeaderProps): JSX.Element => {
  const dispatch = useDispatch();

  const formPageToShow = useSelector(
    (state: AirAmbulanceState) => state.airAmbulance.formPageToShow,
  );

  const hasSelectedCallerID = useSelector(
    (state: EmergencyCallsState) => state.emergencyCalls.selectedCallerID,
  );

  const airAmbulanceRequests = useSelector(
    (state: AirAmbulanceState) => state.airAmbulance.requests,
  );
  const existingRequest =
    hasSelectedCallerID && airAmbulanceRequests
      ? airAmbulanceRequests[hasSelectedCallerID] || null
      : null;

  const handleExit = useCallback(() => {
    if (!existingRequest || formPageToShow === FORM_PAGES.STATUS) {
      dispatch(setSelectedTool(null));
    } else {
      dispatch(setFormPageToShow(FORM_PAGES.STATUS));
    }
    trackCloseAirAmbulanceRequestForm();
  }, [dispatch, existingRequest, formPageToShow]);

  return (
    <Box m={1}>
      <Box horizontal mb={2}>
        {!hideBackButton && <BackButton onClick={handleExit} />}
        <Box>
          <AirAmbulanceSourceInfo
            title={text}
            details={subtext}
            sourceLabel={sourceLabel}
            requestedAt={requestedAt}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AirAmbulancePageHeader;
